import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { ReleasedSurvey } from '@quality24/inpatient-typings';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import { faMessageHeart } from '@fortawesome/pro-regular-svg-icons';
import { Icon, Text } from '@quality24/design-system';
import classNames from 'classnames';
import { handlebarsCompiler } from '@/inpatient-patient-pwa/utils/handlebars';

export type Survey = Pick<
  ReleasedSurvey,
  'id' | 'surveyId' | 'createdAt' | 'status'
> | null;

export interface Props {
  className?: string;
  survey: Survey;
  text?: string;
}

const SurveyNotificationAlert: React.FunctionComponent<Props> = ({
  className,
  survey,
  text,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const surveyDate = moment(survey?.createdAt);
  const surveyDow = surveyDate.format('dddd');
  const currentDow = moment().format('dddd');

  /**
   * Whether the weekday is masculine, which pt-BR needs to agree the connector
   * ("neste sábado" / "nesta quarta-feira"). Derived from the day index — 0 is
   * Sunday and 6 is Saturday — so it does not depend on the language the day
   * name was formatted in.
   */
  const isMasculineDow = [0, 6].includes(surveyDate.day());

  // Only the hospital-configured text goes through the template
  const htmlString = text
    ? ReactHtmlParser(handlebarsCompiler(text)({ surveyDow, currentDow }))
    : null;

  return (
    <button
      className={classNames(className, 'border-0 p-0 bg-transparent text-left')}
      type="button"
      onClick={() => navigate(`/surveys/${survey?.id}`)}
    >
      <div className="d-flex flex-column align-items-start gap-2 bg-primaryLighter rounded-md p-3">
        <Icon icon={faMessageHeart} color="primary" size="md" />

        {!text && (
          <Text as="span" color="blackLight">
            <Trans
              i18nKey="survey.notification.text"
              values={{
                connector: isMasculineDow ? 'e' : 'a',
                day: surveyDow,
              }}
              components={{ strong: <strong /> }}
            />
          </Text>
        )}

        {!!text && (
          <Text as="span" color="blackLight">
            {htmlString}
          </Text>
        )}

        <Text
          as="span"
          className="p-2 bg-primary rounded-md fw-bold"
          color="white"
        >
          {t('survey.notification.cta')}
        </Text>
      </div>
    </button>
  );
};

export default SurveyNotificationAlert;
